import { defineStore } from 'pinia';
import { shouldThrottle } from '@/util/rateLimit.js';
import apiClient from '@/util/apiClient';
import posthog from '@/composables/usePostHog.js';
import logger from '@/util/logging/logger.js';
import type { NumericLike, UserSettingsRecord } from '@/types/assigning';
import { validFirstDayOfWeek, validPageSize } from '@/composables/useUserPreferences';

interface UserSettingsState {
	userSettings: UserSettingsRecord;
	settingId: NumericLike;
}

function toPathSegment(value: NumericLike): string {
	return String(value ?? '');
}

function normalizeUserSettings(settings: unknown): UserSettingsRecord {
	if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
		return {};
	}
	const normalized = { ...(settings as UserSettingsRecord) };
	if ('pageSize' in normalized) {
		normalized.pageSize = validPageSize(normalized.pageSize) ?? null;
	}
	if ('viewMode' in normalized) {
		normalized.viewMode = String(normalized.viewMode).toUpperCase() === 'CARD' ? 'CARD' : 'TABLE';
	}
	if ('firstDayOfWeek' in normalized) {
		normalized.firstDayOfWeek = validFirstDayOfWeek(normalized.firstDayOfWeek) ?? 'SUNDAY';
	}
	if ('settingNumber' in normalized) {
		const settingNumber = normalized.settingNumber == null ? Number.NaN : Number(normalized.settingNumber);
		normalized.settingNumber = Number.isFinite(settingNumber) ? settingNumber : null;
	}
	return normalized;
}

function persistUserSettings(settings: UserSettingsRecord | null) {
	try {
		localStorage.setItem('user_settings', JSON.stringify(settings));
	} catch (error) {
		logger.warn('Failed to persist user settings locally', { error });
	}
}

export const useUserSettingsStore = defineStore('userSettingsStore', {
	state: (): UserSettingsState => ({
		userSettings: {},
		settingId: null
	}),
	actions: {
		async fetchUserSettings(user: NumericLike): Promise<UserSettingsRecord | undefined> {
			if (shouldThrottle('fetchUserSettings', 100)) {
				posthog.capture('throttled_action', { action: 'fetchUserSettings' });
				return;
			}
			try {
				const response = await apiClient.get<UserSettingsRecord>(`/user/settings/user/${toPathSegment(user)}`);
				this.userSettings = normalizeUserSettings(response.data);
				this.settingId = this.userSettings.settingNumber ?? null;
				persistUserSettings(this.userSettings);
				return this.userSettings;
			} catch (error) {
				logger.error('Failed to fetch user settings', { error });
				throw error;
			}
		},
		async createUserSettings(settings: UserSettingsRecord) {
			if (shouldThrottle('createUserSettings', 100)) {
				posthog.capture('throttled_action', { action: 'createUserSettings' });
				return;
			}
			try {
				const response = await apiClient.post<UserSettingsRecord>('/user/settings/save', settings);
				this.userSettings = normalizeUserSettings(response.data);
				this.settingId = this.userSettings.settingNumber ?? null;
				persistUserSettings(this.userSettings);
				return this.userSettings;
			} catch (error) {
				logger.error('Failed to create user settings', { error });
				throw error;
			}
		},
		async saveUserSettings(settingId: NumericLike, settings: UserSettingsRecord) {
			if (shouldThrottle('saveUserSettings', 100)) {
				posthog.capture('throttled_action', { action: 'saveUserSettings' });
				return;
			}
			try {
				const response = await apiClient.patch<UserSettingsRecord>(`/user/settings/edit/${toPathSegment(settingId)}`, settings);
				this.userSettings = normalizeUserSettings(response.data);
				this.settingId = this.userSettings.settingNumber ?? null;
				persistUserSettings(this.userSettings);
				return this.userSettings;
			} catch (error) {
				logger.error('Failed to save user settings', { error });
				throw error;
			}
		}
	}
});
